import { useMutation, useReactiveVar } from "@apollo/client";
import { CreatePremiumServiceInterestForm } from "components/forms";
import {
  CreatePremiumServiceInterestSchema,
  getCreatePremiumServiceInterestInitialValues,
} from "components/forms/premium-service-interest/schema";
import { Modal } from "components/layouts";
import { currentDistrictVar } from "apollo/cache/auth";
import { useFormik } from "formik";
import { FC } from "react";
import toast from "react-hot-toast";
import { wrapClick } from "utils";
import { CREATE_PREMIUM_SERVICE_INTEREST } from "./apollo";

interface CreatePremiumServiceInterestContainerProps {
  open: boolean;
  setOpen: (val: boolean) => void;
  refetch?: () => void;
}

const CreatePremiumServiceInterestContainer: FC<
  CreatePremiumServiceInterestContainerProps
> = ({ open, setOpen, refetch }) => {
  const currentDistrict = useReactiveVar(currentDistrictVar);
  const [createPremiumServiceInterest, { loading }] = useMutation(
    CREATE_PREMIUM_SERVICE_INTEREST,
  );

  const form = useFormik({
    initialValues: getCreatePremiumServiceInterestInitialValues(
      currentDistrict ?? undefined,
    ),
    enableReinitialize: true,
    validationSchema: CreatePremiumServiceInterestSchema,
    onSubmit: async (values) => {
      await createPremiumServiceInterest({
        variables: {
          district: values.district || undefined,
          customer: values.customer,
        },
      }).then(({ data }) => {
        if (data?.createPremiumServiceInterest?._id) {
          toast(
            JSON.stringify({
              type: "success",
              title: "Expression of Interest Created Successfully",
            }),
          );
          refetch?.();
          form.resetForm();
          setOpen(false);
        } else {
          toast(
            JSON.stringify({
              type: "error",
              title: "Could not create Expression of Interest",
            }),
          );
        }
      });
    },
    onReset: () => {
      setOpen(false);
    },
  });

  return (
    <Modal
      open={open}
      setOpen={setOpen}
      title="Create Expression of Interest"
      description="Provide customer details to register an expression of interest"
      renderActions={() => (
        <button
          type="button"
          disabled={loading}
          className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-primary-600 text-base font-medium text-white hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:ml-3 sm:w-auto sm:text-sm"
          onClick={wrapClick(form.handleSubmit)}
        >
          {loading ? "Registering..." : "Register Expression of Interest"}
        </button>
      )}
    >
      <CreatePremiumServiceInterestForm form={form} />
    </Modal>
  );
};

export default CreatePremiumServiceInterestContainer;
